'use client'

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { LOAN_TYPES, LOAN_VALIDATION, formatDurationToYearsAndMonths } from '@/app/schemas/loan-comparator/loan.schema'
import { computeAutoStageAmount } from '@/app/utils/loanCalculations'
import AppNumericInput from '@/app/components/atomic/Input/AppNumericInput'
import AppTooltipWrapper from '@/app/components/atomic/molecules/AppTooltipWrapper'

// Parse the formatted string returned by AppNumericInput into a number (or undefined).
const parseStageNumber = (value: string | number | null | undefined): number | undefined => {
    const n = parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'))
    return Number.isNaN(n) ? undefined : n
}

interface Stage {
    id: number
    amount: number | undefined
    month_duration: number | undefined
}

interface InitialStage {
    id?: number
    montant?: number | null
    duree?: number | null
    sum?: number
    period?: number
    amount?: number
    month_duration?: number
    sum_alt_currency?: number | null
}

interface Props {
    loanIndex: number
    loanType: number
    loanAmount: number | undefined
    loanDuration: number | undefined
    loanRate: number
    delayDuration: number | undefined
    currencySymbol?: string
    initialStages?: InitialStage[]
    onStagesChange?: (stages: { amount: number | undefined; month_duration: number | undefined }[]) => void
    className?: string
}

export default function LoanComparatorPalierForm({
    loanIndex,
    loanType,
    loanAmount,
    loanDuration,
    loanRate,
    delayDuration = 0,
    currencySymbol = '€',
    initialStages = [],
    onStagesChange,
    className = '',
}: Props) {
    const initialMappedStages = useMemo((): Stage[] => {
        if (!initialStages || initialStages.length === 0) return []

        const mapped = initialStages.map((s, index) => ({
            id: s.id || index + 1,
            // `||` on purpose: legacy CHF stages store 0 in sum_alt_currency and the amount in montant.
            amount: s.sum_alt_currency || (s.montant ?? s.sum ?? s.amount ?? undefined),
            month_duration: s.duree ?? s.period ?? s.month_duration ?? undefined,
        }))

        return mapped
    }, [initialStages])

    const [stages, setStages] = useState<Stage[]>(initialMappedStages)
    const [stageIdCounter, setStageIdCounter] = useState(() => {
        if (initialMappedStages.length === 0) return 1
        return Math.max(...initialMappedStages.map((s) => s.id)) + 1
    })

    const prevLoanTypeRef = useRef<number | undefined>(loanType)

    useEffect(() => {
        if (initialMappedStages.length > 0) {
            setStages(initialMappedStages)
            const maxId = Math.max(...initialMappedStages.map((s) => s.id))
            setStageIdCounter(maxId + 1)
        }
    }, [initialMappedStages])

    useEffect(() => {
        const isStage = loanType === LOAN_TYPES.STAGE
        const wasStaged =
            prevLoanTypeRef.current === LOAN_TYPES.STAGE || prevLoanTypeRef.current === LOAN_TYPES.BUILDING
        const typeChanged = prevLoanTypeRef.current !== undefined && prevLoanTypeRef.current !== loanType

        prevLoanTypeRef.current = loanType

        // Un switch depuis un type à paliers vide les paliers (VEFA doit démarrer sans palier)
        if (typeChanged && wasStaged) {
            setStages(
                isStage
                    ? [
                          { id: 1, amount: undefined, month_duration: undefined },
                          { id: 2, amount: undefined, month_duration: undefined },
                      ]
                    : []
            )
            setStageIdCounter(isStage ? 3 : 1)
            return
        }

        if (isStage && stages.length === 0 && initialMappedStages.length === 0) {
            setStages([
                { id: 1, amount: undefined, month_duration: undefined },
                { id: 2, amount: undefined, month_duration: undefined },
            ])
            setStageIdCounter(3)
        }
    }, [loanType, stages.length, initialMappedStages.length])

    const isLoanValid = useMemo(() => {
        return (
            loanAmount !== undefined &&
            loanAmount > 0 &&
            loanDuration !== undefined &&
            loanDuration > 0 &&
            loanRate !== undefined
        )
    }, [loanAmount, loanDuration, loanRate])

    const effectiveDuration = useMemo(() => {
        if (!loanDuration) return 0
        return Math.max(0, loanDuration - (delayDuration || 0))
    }, [loanDuration, delayDuration])

    const stageTotals = useMemo(() => {
        const stagesToSum = stages.slice(0, -1)
        return stagesToSum.reduce(
            (acc, stage) => {
                const amount = stage.amount || 0
                const duration = stage.month_duration || 0
                acc.totalAmount += amount * duration
                acc.totalDuration += duration
                return acc
            },
            { totalAmount: 0, totalDuration: 0 }
        )
    }, [stages])

    const lastStageValues = useMemo(() => {
        if (!isLoanValid || stages.length < 2) {
            return { amount: 0, duration: 0 }
        }

        const remainingDuration = effectiveDuration - stageTotals.totalDuration
        if (remainingDuration <= 0) {
            return { amount: 0, duration: 0 }
        }

        const monthlyPayment = computeAutoStageAmount(loanAmount || 0, loanRate, stages.slice(0, -1), remainingDuration)

        return {
            amount: Math.round(monthlyPayment * 100) / 100,
            duration: remainingDuration,
        }
    }, [isLoanValid, stages, effectiveDuration, stageTotals, loanAmount, loanRate])

    useEffect(() => {
        if (onStagesChange) {
            const stagesToReport = stages.map((s, index) => {
                const isLast = index === stages.length - 1
                if (isLast) {
                    return {
                        amount: lastStageValues.amount || 0,
                        month_duration: lastStageValues.duration || 0,
                    }
                }
                return {
                    amount: s.amount,
                    month_duration: s.month_duration,
                }
            })
            onStagesChange(stagesToReport)
        }
    }, [stages, onStagesChange, lastStageValues])

    const addStage = useCallback(() => {
        if (stages.length >= LOAN_VALIDATION.MAX_STAGES) return

        const newStage: Stage = {
            id: stageIdCounter,
            amount: undefined,
            month_duration: undefined,
        }

        if (stages.length === 0) {
            setStages([newStage, { id: stageIdCounter + 1, amount: undefined, month_duration: undefined }])
            setStageIdCounter((prev) => prev + 2)
        } else {
            const newStages = [...stages]
            newStages.splice(stages.length - 1, 0, newStage)
            setStages(newStages)
            setStageIdCounter((prev) => prev + 1)
        }
    }, [stages, stageIdCounter])

    const initBuildingStages = useCallback(() => {
        setStages([
            { id: 1, amount: undefined, month_duration: undefined },
            { id: 2, amount: undefined, month_duration: undefined },
        ])
        setStageIdCounter(3)
    }, [])

    const removeStage = useCallback((stageId: number) => {
        setStages((prev) => prev.filter((s) => s.id !== stageId))
    }, [])

    const resetStages = useCallback(() => {
        setStages([])
        setStageIdCounter(1)
    }, [])

    const updateStage = useCallback((stageId: number, field: keyof Stage, value: number | undefined) => {
        setStages((prev) => prev.map((stage) => (stage.id === stageId ? { ...stage, [field]: value } : stage)))
    }, [])

    const isLastStage = useCallback(
        (index: number) => {
            return index === stages.length - 1
        },
        [stages.length]
    )

    const stagesAreValid = useMemo(() => {
        if (stages.length < 2) return false
        return stages
            .slice(0, -1)
            .every(
                (stage) =>
                    stage.amount !== undefined &&
                    stage.amount >= 0 &&
                    stage.month_duration !== undefined &&
                    stage.month_duration > 0
            )
    }, [stages])

    const durationExceedsLoan = stageTotals.totalDuration > effectiveDuration

    const isVEFA = loanType === LOAN_TYPES.BUILDING

    return (
        <div className={`mt-6 border-t border-blue-200 pt-6 ${className}`}>
            {/* Header : titre à gauche, bouton d'ajout à droite quand il n'y a pas encore de palier */}
            <div className="mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-[14px] font-medium text-pale m-0">
                        Renseignez les paliers de votre prêt <span className="underline">hors assurance</span>
                    </p>
                    {stages.length === 0 && (
                        <button
                            type="button"
                            onClick={isVEFA ? initBuildingStages : addStage}
                            className="inline-flex items-center gap-2.5 px-5 py-[15px] bg-rm-blue rounded text-[14px] text-white transition-opacity hover:opacity-90"
                        >
                            <i className="fa-regular fa-plus" />
                            {isVEFA ? 'Ajouter des paliers' : 'Ajouter un palier'}
                        </button>
                    )}
                </div>
                {!isLoanValid && (
                    <p className="text-[12px] text-blue-500 mt-1">
                        <i className="fa-regular fa-info-circle mr-1" />
                        Nous avons besoin du montant, de la durée et du taux d&apos;intérêt de votre prêt pour vous aider à
                        renseigner vos paliers
                    </p>
                )}
            </div>

            {/* Stages list */}
            {stages.length > 0 && (
                <div>
                    {/* Warning if total exceeds loan */}
                    {durationExceedsLoan && (
                        <div className="text-center text-red-600 text-[12px] font-medium mb-4">
                            Les paliers ne correspondent pas au montant emprunté avec intérêts.
                        </div>
                    )}

                    {/* Stage rows */}
                    <div className="space-y-3">
                        {stages.map((stage, index) => {
                            const isLast = isLastStage(index)
                            const dur = isLast ? lastStageValues.duration : stage.month_duration

                            return (
                                <div key={stage.id} className="flex items-end gap-3">
                                    {/* Montant */}
                                    <div className="flex-1">
                                        <AppTooltipWrapper
                                            id={`palier-amount-tooltip-${loanIndex}-${stage.id}`}
                                            title="Montant du palier"
                                            text="Mensualité <u>hors assurance</u> que vous remboursez pendant ce palier. Le <strong>dernier palier est calculé automatiquement</strong> à partir du capital restant dû."
                                            iconClassName="fa-info-circle"
                                            iconType="regular"
                                            theme="loan"
                                            className="mb-2.5 flex items-center justify-between gap-1.5"
                                        >
                                            <label className="text-[14px] font-medium text-pale">
                                                Palier {index + 1} (en {currencySymbol})
                                                {isLast && (
                                                    <span className="text-[12px] font-normal text-blue-500 italic">
                                                        {' '}(Automatique)
                                                    </span>
                                                )}
                                            </label>
                                        </AppTooltipWrapper>
                                        <AppNumericInput
                                            fieldName={`stage-amount-${stage.id}`}
                                            value={isLast ? lastStageValues.amount : (stage.amount ?? '')}
                                            borders="neutral"
                                            thousandSeparator=" "
                                            placeholder="0"
                                            disabled={!isLoanValid || isLast}
                                            onChange={({ value }) =>
                                                updateStage(stage.id, 'amount', parseStageNumber(value))
                                            }
                                        />
                                    </div>

                                    {/* Durée */}
                                    <div className="flex-1">
                                        <AppTooltipWrapper
                                            id={`palier-duration-tooltip-${loanIndex}-${stage.id}`}
                                            title="Durée du palier"
                                            text="Nombre de mois pendant lesquels cette mensualité s'applique. La somme des durées ne peut pas dépasser la durée totale du prêt, différé compris."
                                            iconClassName="fa-info-circle"
                                            iconType="regular"
                                            theme="loan"
                                            className="mb-2.5 flex items-center justify-between gap-1.5"
                                        >
                                            <label className="text-[14px] font-medium text-pale">Durée (en mois)</label>
                                        </AppTooltipWrapper>
                                        <div className="relative">
                                            <AppNumericInput
                                                fieldName={`stage-duration-${stage.id}`}
                                                value={isLast ? lastStageValues.duration : (stage.month_duration ?? '')}
                                                borders="neutral"
                                                thousandSeparator={false}
                                                placeholder="0"
                                                disabled={!isLoanValid || isLast}
                                                className="pr-20"
                                                onChange={({ value }) =>
                                                    updateStage(stage.id, 'month_duration', parseStageNumber(value))
                                                }
                                            />
                                            {Number.isFinite(dur) && (dur as number) >= 12 && (
                                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-blue-500">
                                                    ={formatDurationToYearsAndMonths(dur as number)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Remove button (not for first or last stage) */}
                                    {index !== 0 && !isLast ? (
                                        <button
                                            type="button"
                                            onClick={() => removeStage(stage.id)}
                                            className="shrink-0 w-8 h-12 flex items-center justify-center text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded transition-colors"
                                            title="Supprimer ce palier"
                                        >
                                            <i className="fa-regular fa-trash-alt text-[12px]" />
                                        </button>
                                    ) : (
                                        <div className="shrink-0 w-8" />
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {/* Validation warning */}
                    {!stagesAreValid && stages.length > 0 && (
                        <p className="text-center text-[12px] text-orange-600 font-bold mt-4">
                            Veuillez d&apos;abord remplir correctement les paliers présents avant d&apos;en ajouter.
                        </p>
                    )}

                    {/* Add stage button */}
                    {stages.length > 0 && stages.length < LOAN_VALIDATION.MAX_STAGES && (
                        <div className="flex justify-center gap-4 mt-5">
                            <div
                                className={`flex items-center justify-center ${
                                    stagesAreValid ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                                }`}
                                onClick={stagesAreValid ? addStage : undefined}
                            >
                                <button
                                    type="button"
                                    disabled={!stagesAreValid}
                                    className="inline-flex items-center gap-2.5 rounded bg-rm-blue px-5 py-3 text-[15px] text-white transition-opacity hover:opacity-90"
                                >
                                    <i className="fa-regular fa-plus text-[13px]" />
                                    Ajouter un palier
                                </button>
                            </div>

                            {isVEFA && stages.length > 0 && (
                                <button
                                    type="button"
                                    onClick={resetStages}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors text-[14px] font-medium"
                                >
                                    <i className="fa-regular fa-trash-alt" />
                                    Supprimer les paliers
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
